import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { AuthenticatedUser } from '@quorum/authentication/state';
import { DeepCopyPipe } from '@quorum/common-pipes';
import {
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  AssociateQueryParameters,
} from '@quorum/models/xs-query';
import {
  Address,
  ServiceAppointment as Appointment,
  Associate,
  AssociateReport,
  Customer,
  Employee,
  Location,
  MessageType,
  SystemControlValue,
  Vehicle,
  VehicleBody,
} from '@quorum/models/xs-resource';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { VehicleOwnerCreateDialogComponent } from 'libs/sha-associates-ng-ui/src/lib/vehicle-owner-create-dialog/vehicle-owner-create-dialog.component';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
// eslint-disable-next-line max-len
import { AppointmentConfirmDeleteDialogComponent } from '../appointment-confirm-delete-dialog/appointment-confirm-delete-dialog.component';

@Component({
  selector: 'svc-vehicle',
  templateUrl: './vehicle.component.html',
  styleUrls: ['./vehicle.component.scss'],
})
export class VehicleComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  displayNewAppointmentForm: Boolean = false;
  newAppointment$: BehaviorSubject<Appointment> = new BehaviorSubject(new Appointment());
  updateAssociateSub: Subscription;
  updateAddressSub: Subscription;
  updateVehicleSub: Subscription;
  createAppointmentSub: Subscription;
  deleteAppointmentSub: Subscription;

  openAppointments$: Observable<Appointment[]>;

  editMode: boolean = false;
  vehicleForm: FormGroup;
  associateForm: FormGroup;
  addressForm: FormGroup;
  vehicleImage: SafeResourceUrl;
  systemControl$: Observable<Record<string, SystemControlValue>>;
  locations$: any;
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  serviceAppointmentDropdowns$: Observable<any>;
  listenForCreateAppointmentOnServerSuccessSub: Subscription;
  appointmentForm: FormGroup;
  appointmentFormInvalid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  typedName: string;

  bodyDropdowns$: Observable<VehicleBody[]>;
  associateFormCombinedSub: Subscription;
  authenticatedUser$: Observable<AuthenticatedUser>;
  authenicatedEmployee$: Observable<Employee>;

  filteredAssociates: AssociateReport[] = [];
  selectedAssoc: AssociateReport;
  searchedAssociateName: string = '';
  vehicleFormNameSub: Subscription;

  constructor(
    private sanitizer: DomSanitizer,
    private apiService: ApiService,
    public serviceLaneFacade: ServiceLaneFacade,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    private router: Router,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((emp) => emp != null),
        take(1)
      )
      .subscribe((employee) => {
        this.systemControl$ = this.systemControlStateService
          .getSystemControlValues(
            [
              'CL_SERV_APPT_ASSIGN_TECH_GRP_NAME',
              'CL_COMMUNICATOR_ENABLE',
              'CL_CUSTOMER_USING_SOFTWARE',
              'CL_DEFAULT_SERVICE_CODE',
            ],
            employee.rooftopId
          )
          .pipe(take(1));
      });
    this.authenicatedEmployee$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(take(1));
    this.authenticatedUser$ = this.authenticationStateService.selectAuthenticatedUser().pipe(
      filter((authenticatedUser) => authenticatedUser != null),
      take(1)
    );

    this.openAppointments$ = this.serviceLaneFacade.openAppointmentsLoading$.pipe(
      filter((status) => status === 'succeeded'),
      switchMap((status) => this.serviceLaneFacade.openAppointments$),
      tap((appointment) => {
        if (appointment.length === 0) {
          combineLatest([
            this.serviceLaneFacade.vehicle$.pipe(filter((vehicle) => vehicle != null)),
            this.serviceLaneFacade.customerAssociate$.pipe(filter((customer) => customer != null)),
            this.authenticationStateService.selectAuthenticatedEmployee().pipe(take(1)),
            this.systemControl$.pipe(take(1)),
          ])
            .pipe(
              map(([vehicle, associate, currentEmployee, systemControls]) => {
                return { vehicle, associate, currentEmployee, systemControls };
              })
            )
            .subscribe((data) => {
              this.toggleAppointmentForm(
                data.vehicle,
                data.associate,
                this.displayNewAppointmentForm,
                data.systemControls,
                data.currentEmployee
              );
            });
        }
      })
    );

    this.updateAssociateSub = this.serviceLaneFacade.updateAssociate$.subscribe();
    this.updateAddressSub = this.serviceLaneFacade.updateAddress$.subscribe();
    this.updateVehicleSub = this.serviceLaneFacade.updateVehicle$.subscribe();
    this.createAppointmentSub = this.serviceLaneFacade.createAppointment$.subscribe();
    this.deleteAppointmentSub = this.serviceLaneFacade.deleteAppointment$.subscribe();

    this.listenForCreateAppointmentOnServerSuccessSub =
      this.serviceLaneFacade.listenForCreateAppointmentOnServerSuccess$
        .pipe(
          map((appointment) => {
            return appointment;
          }),
          take(1)
        )
        .subscribe((appointment) => {
          this.router.navigate(['vehicle', appointment.vehicleId, 'appointments', appointment.id]);
        });

    this.serviceLaneFacade.vehicle$
      .pipe(
        filter((vehicle) => vehicle != null),
        take(1)
      )
      .subscribe((vehicle) => {
        this.apiService
          .get(`/v/1/reporting/vehicles/image/${vehicle.vin}?size=large`)
          .pipe(take(1))
          .subscribe((photo) => {
            this.vehicleImage = this.sanitizer.bypassSecurityTrustResourceUrl(
              `data:image/jpg;base64,${photo.file_contents}`
            );
          });
      });

    this.locations$ = new BehaviorSubject<any>(null).pipe(
      switchMap((provStateId: string) => {
        if (provStateId) {
          return this.serviceLaneDropdownFacade.locationsDropdowns$.pipe(
            map((locations) => locations.filter((locations) => locations.provStateId === provStateId))
          );
        } else {
          return of([]);
        }
      }),
      shareReplay(1)
    );

    this.serviceAppointmentDropdowns$ = combineLatest(
      this.serviceLaneDropdownFacade.serviceAppointmentDropdowns$,
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((emp) => emp != null),
        switchMap((employee) => {
          return this.systemControlStateService
            .getSystemControlValues(['CL_GM_CADILLAC_SERVICE_CENTER'], employee.rooftopId)
            .pipe(startWith(null));
        })
      ),
      this.serviceLaneFacade.vehicle$.pipe(filter((vehicle) => vehicle != null))
    ).pipe(
      map(([dropdowns, sysControl, vehicle]) => {
        let dropdownsCopy = new DeepCopyPipe().transform(dropdowns);
        if (
          (sysControl && sysControl['CL_GM_CADILLAC_SERVICE_CENTER'].value !== '1') ||
          vehicle.make.toLowerCase() !== 'cadillac'
        ) {
          dropdownsCopy.transportOptions = dropdowns.transportOptions.filter(
            (transportOption) => !transportOption.name.toLowerCase().includes('cadillac premium concierge')
          );
        }
        return dropdownsCopy;
      })
    );
  }

  openAppointment(appointment: Appointment) {
    this.router.navigate([`vehicle`, appointment.vehicleId, `appointments`, appointment.id]);
  }

  newAppointment(displayNewAppointmentForm: Boolean) {
    //this.displayNewAppointmentForm = !displayNewAppointmentForm;
  }

  cancelNewAppointment(displayNewAppointmentForm: Boolean) {
    this.displayNewAppointmentForm = !displayNewAppointmentForm;
  }

  saveNewAppointment(displayNewAppointmentForm: Boolean) {
    this.displayNewAppointmentForm = !displayNewAppointmentForm;
  }

  appointmentModified(appointment: Appointment) {}

  updateAssociate(associate: Associate) {
    this.serviceLaneFacade.updateAssociateOnServer(associate);
  }

  updateAddress(address: Address) {
    this.serviceLaneFacade.updateAddressOnServer(address);
  }

  updateVehicle(vehicle: Vehicle) {
    this.serviceLaneFacade.updateVehicleOnServer(vehicle);
  }

  createAppointment(appointment: Appointment) {
    const appointmentTime: string = this.appointmentForm.get('appointmentTime').value;
    const dateTime = new Date(appointment.dateTime);

    if (appointmentTime) {
      dateTime.setHours(parseInt(appointmentTime.split(':')[0]), parseInt(appointmentTime.split(':')[1]), 0);
      appointment.dateTime = dateTime;
    }

    appointment = {
      ...appointment,
      odometerIn:
        appointment.odometerIn === null ||
        (appointment.odometerIn !== null && appointment.odometerIn.toString().length == 0)
          ? null
          : appointment.odometerIn,
      dateTime: dateTime,
      cadillacConciergeDropoff: this.appointmentForm.get('cadillacConciergeUsePickupLocation').value
        ? this.appointmentForm.get('cadillacConciergePickup').value
        : this.appointmentForm.get('cadillacConciergeDropoff').value,
      cadillacConciergeContactPhone:
        this.appointmentForm.get('cadillacConciergeContactPhone').value != null
          ? this.appointmentForm.get('cadillacConciergeContactPhone').value.replace(/\D+/g, '')
          : null,
    };

    if (appointment?.promisedDateTime) {
      let promisedDate = new Date(appointment.dateTime);
      promisedDate.setHours(appointment?.promisedDateTime.getHours(), appointment?.promisedDateTime.getMinutes(), 0);
      appointment.promisedDateTime = promisedDate;
    }

    let queryParams = new AppointmentQueryParameters({
      embed: 'serviceAdvisorAssociate',
    });

    this.serviceLaneFacade.createAppointmentOnServer(appointment, queryParams);
    this.saveNewAppointment(this.displayNewAppointmentForm);
  }

  appointmentFormEmit(appointmentForm: FormGroup) {
    this.appointmentForm = appointmentForm;
    this.appointmentFormInvalid$.next(this.appointmentForm.invalid);
    this.cdr.detectChanges();
  }

  toggleAppointmentForm(
    vehicle: Vehicle,
    associate: Associate,
    displayNewAppointmentForm: Boolean,
    systemControls: any,
    currentEmployee: any
  ) {
    if (!displayNewAppointmentForm) {
      let appointment: Appointment = {
        ...new Appointment(),
        vehicleDescription: `${vehicle.year} ${vehicle.model}`,
        vehicleId: vehicle.id,
        vehicleMake: vehicle.make,
        vehicleVin: vehicle.vin,
        vehicleYear: vehicle.year.toString(),
        customerId: associate.id,
        customerName: `${associate.lastName}, ${associate.firstName}`,
        employeeId: currentEmployee.associateId,
        licenseNumber: vehicle.licensePlateNumber,
        rooftopId: currentEmployee.rooftopId,
        statusId: 1,
        totalCases: 0,
        dateIn: new Date(),
        dateTime: new Date(),
        technicianGroupId:
          systemControls.CL_SERV_APPT_ASSIGN_TECH_GRP_NAME &&
          systemControls.CL_SERV_APPT_ASSIGN_TECH_GRP_NAME.value !== null
            ? parseInt(systemControls.CL_SERV_APPT_ASSIGN_TECH_GRP_NAME.value)
            : null,
        technicianGroupName:
          systemControls.CL_SERV_APPT_ASSIGN_TECH_GRP_NAME &&
          systemControls.CL_SERV_APPT_ASSIGN_TECH_GRP_NAME.displayValue !== ''
            ? systemControls.CL_SERV_APPT_ASSIGN_TECH_GRP_NAME.displayValue
            : null,
        serviceCode:
          systemControls.CL_DEFAULT_SERVICE_CODE && systemControls.CL_DEFAULT_SERVICE_CODE.value != ''
            ? systemControls.CL_DEFAULT_SERVICE_CODE.value
            : null,
      };
      this.newAppointment$.next(appointment);
      this.displayNewAppointmentForm = !displayNewAppointmentForm;
    } else {
      this.displayNewAppointmentForm = !displayNewAppointmentForm;
    }
  }

  updateFormResources(vehicle: Vehicle, associate: Associate, address: Address) {
    this.editMode = !this.editMode;
    address = {
      ...address,
      cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
      homePhoneNumber: address.homePhoneNumber ? address.homePhoneNumber.replace(/\D+/g, '') : null,
    };
    this.serviceLaneFacade.updateVehicleOnServer(vehicle);
    this.serviceLaneFacade.updateAssociateOnServer(associate);
    this.serviceLaneFacade.updateAddressOnServer(address);
  }

  enableEditMode(vehicle: Vehicle, associate: Associate, address: Address) {
    if (!vehicle || !associate || !address) {
      return;
    }
    this.vehicleForm = createFormGroupFromObject(vehicle);

    this.bodyDropdowns$ = combineLatest(
      this.apiService.get<VehicleBody[]>(`v/1/vehicles/vehicle-bodies`),
      this.vehicleForm.get('body').valueChanges.pipe(
        filter((value) => value.length > 1),
        startWith(vehicle.body)
      )
    ).pipe(
      map(([bodies, value]) => {
        return bodies.filter((body: any) => body.vehicleBody.toLowerCase().includes(value.toLowerCase()));
      })
    );

    this.vehicleForm.addControl('name', new FormControl(''));
    this.vehicleForm.get('name').setValue(`${associate.lastName}, ${associate.firstName}`);
    this.vehicleFormNameSub = this.vehicleForm['controls'].name.valueChanges
      .pipe(
        filter((input: string) => {
          if (input) {
            return input.length >= 3;
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: any) => {
          let params: AssociateQueryParameters = new AssociateQueryParameters({
            search: value,
          });
          this.searchedAssociateName = value;
          return this.apiService.get<AssociateReport[]>('v/1/reporting/associates', { params: params }).pipe(
            map((associates) => {
              this.filteredAssociates = associates;
              this.associateForm
                .get('firstName')
                .setValue(
                  this.vehicleForm.get('name').value.split(',')[1]
                    ? this.vehicleForm.get('name').value.split(',')[1].trim()
                    : this.vehicleForm.get('name').value
                );
              this.associateForm
                .get('lastName')
                .setValue(
                  this.vehicleForm.get('name').value.split(',')[1]
                    ? this.vehicleForm.get('name').value.split(',')[0].trim()
                    : null
                );
            }),
            catchError((error) => {
              this.vehicleForm.enable({ emitEvent: false });
              return of(error);
            })
          );
        })
      )
      .subscribe();

    this.setVehicleFormValidation();

    this.associateForm = createFormGroupFromObject(associate);

    this.setAssociateFormValidation();
    this.addressForm = createFormGroupFromObject(address);
    this.locations$.next(address.provStateId);
    this.addressForm.get('provStateId').valueChanges.subscribe((provState: any) => {
      this.locations$.next(provState);
      this.addressForm.get('locationId').patchValue(null);
      this.addressForm.get('locationId').setValidators(Validators.required);
      this.addressForm.updateValueAndValidity();
      this.addressForm.markAllAsTouched();
    });

    this.associateFormCombinedSub = combineLatest(
      this.serviceLaneDropdownFacade.associateDropdowns$,
      this.associateForm.get('messagePreferenceId').valueChanges
    )
      .pipe(
        map(([appointmentDropdowns, messagePreference]) => {
          return { messageTypes: appointmentDropdowns.messageTypes, messagePreference };
        })
      )
      .subscribe((combinedData) => {
        if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'email'
        ) {
          this.associateForm.get('emailAddress').setValidators([Validators.email, Validators.required]);
          this.associateForm.get('emailAddress').markAsTouched();
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        } else if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'text'
        ) {
          this.addressForm.get('cellPhoneNumber').setValidators(Validators.required);
          this.addressForm.get('cellPhoneNumber').markAsTouched();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
        } else {
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        }
      });

    this.editMode = true;
  }

  deleteAppointment(appointment: Appointment, event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const dialogRef = this.dialog.open(AppointmentConfirmDeleteDialogComponent, {
      width: '400px',
      data: {},
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result == 'yes') {
        this.serviceLaneFacade.deleteServiceAppointmentFromServer(appointment);
      }
    });
  }

  setVehicleFormValidation() {
    this.vehicleForm.get('name').setValidators(Validators.required);

    this.vehicleForm
      .get('year')
      .setValidators([Validators.pattern('^[0-9]+$'), Validators.minLength(4), Validators.maxLength(4)]);
    this.vehicleForm.get('currentMileage').setValidators([Validators.pattern('^[0-9]+$')]);
  }

  setAssociateFormValidation() {
    this.associateForm.get('emailAddress').setValidators([Validators.email]);
  }

  displayAssociateName(filteredAssociates: AssociateReport[], value: string) {
    let displayAssociate: AssociateReport;
    if (filteredAssociates.length > 0) {
      displayAssociate = filteredAssociates.find((associate: AssociateReport) => associate.id === value);
      if (displayAssociate) {
        return displayAssociate.firstName
          ? `${displayAssociate.lastName}, ${displayAssociate.firstName}`
          : `${displayAssociate.fullName}`;
      } else {
        return value;
      }
    } else {
      return value;
    }
  }

  selectAssociate(value: any, filteredAssociates?: AssociateReport[], currentAssociate?: Associate) {
    if (value == 'addAssociate') {
      this.selectedAssoc = null;
      this.vehicleForm.get('name').setValue(null);

      const dialogRef = this.dialog.open(VehicleOwnerCreateDialogComponent, {
        disableClose: true,
        width: '800px',
        data: {
          associateDropdowns$: this.serviceLaneDropdownFacade.associateDropdowns$,
          addressDropdowns$: this.serviceLaneDropdownFacade.addressDropdowns$,
          locations$: this.locations$,
          currentAssociate$: this.serviceLaneFacade.customerAssociate$,
          currentAddress$: this.serviceLaneFacade.customerAddress$,
          typedName: this.typedName ? this.typedName : '',
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        this.autocompleteTrigger.closePanel();

        this.typedName = '';
        if (result) {
          this.apiService
            .post<Associate>(`v/1/associates/associates`, result.newAssociate)
            .subscribe((newAssociate: Associate) => {
              let associateId = newAssociate.id;
              if (!result.newAddress.locationId.id) {
                let newLocation: Location = {
                  ...new Location(),
                  countryId: result.newAddress.countryId,
                  description: result.newAddress.locationId,
                  postalCode: null,
                  provStateId: result.newAddress.provStateId,
                };

                this.apiService
                  .post<Location>('v/1/associates/locations', newLocation)
                  .subscribe((newlyCreatedLocation) => {
                    let updatedAddress: Address = {
                      ...result.newAddress,
                      associateId: associateId,
                      locationId: newlyCreatedLocation.id,
                      cellPhoneNumber: result.newAddress.cellPhoneNumber
                        ? result.newAddress.cellPhoneNumber.replace(/\D+/g, '')
                        : null,
                      homePhoneNumber: result.newAddress.homePhoneNumber
                        ? result.newAddress.homePhoneNumber.replace(/\D+/g, '')
                        : null,
                    };

                    this.apiService
                      .post<Address>(`v/1/associates/associates/${associateId}/addresses`, updatedAddress)
                      .subscribe((newAddress: Address) => {
                        this.associateForm.patchValue(
                          {
                            ...this.associateForm,
                            id: newAssociate.id,
                            firstName: newAssociate.firstName,
                            lastName: newAssociate.lastName,
                            emailAddress: newAssociate.emailAddress,
                            classId: newAssociate.classId,
                            messagePreferenceId: newAssociate.messagePreferenceId,
                          },
                          { emitEvent: false }
                        );
                        this.addressForm.patchValue({
                          ...this.addressForm,
                          associateId: newAssociate.id,
                          addressLine1: newAddress.addressLine1,
                          homePhoneNumber: newAddress.homePhoneNumber,
                          cellPhoneNumber: newAddress.cellPhoneNumber,
                          countryId: newAddress.countryId,
                          postalZipCode: newAddress.postalZipCode,
                          provStateId: newAddress.provStateId,
                          locationId: newAddress.locationId, //new
                        });

                        this.vehicleForm.patchValue({
                          associateId: newAssociate.id,
                          name: `${newAssociate.lastName}, ${newAssociate.firstName}`,
                        });

                        let customer: Customer = {
                          ...new Customer(),
                          associateId: newAssociate.id,
                        };

                        this.apiService
                          .post<Customer>(`v/1/associates/customers`, customer)
                          .subscribe((customer) => {});

                        this.serviceLaneDropdownFacade.getLocationsFromServer();
                      });
                  });
              } else {
                let updatedAddress: Address = {
                  ...result.newAddress,
                  associateId: associateId,
                  locationId: result.newAddress.locationId.id,
                  cellPhoneNumber: result.newAddress.cellPhoneNumber
                    ? result.newAddress.cellPhoneNumber.replace(/\D+/g, '')
                    : null,
                  homePhoneNumber: result.newAddress.homePhoneNumber
                    ? result.newAddress.homePhoneNumber.replace(/\D+/g, '')
                    : null,
                };

                this.apiService
                  .post<Address>(`v/1/associates/associates/${associateId}/addresses`, updatedAddress)
                  .subscribe((newAddress: Address) => {
                    this.associateForm.patchValue(
                      {
                        ...this.associateForm,
                        id: newAssociate.id,
                        firstName: newAssociate.firstName,
                        lastName: newAssociate.lastName,
                        emailAddress: newAssociate.emailAddress,
                        classId: newAssociate.classId,
                        messagePreferenceId: newAssociate.messagePreferenceId,
                      },
                      { emitEvent: false }
                    );
                    this.addressForm.patchValue({
                      ...this.addressForm,
                      associateId: newAssociate.id,
                      addressLine1: newAddress.addressLine1,
                      homePhoneNumber: newAddress.homePhoneNumber,
                      cellPhoneNumber: newAddress.cellPhoneNumber,
                      countryId: newAddress.countryId,
                      postalZipCode: newAddress.postalZipCode,
                      provStateId: newAddress.provStateId,
                      locationId: newAddress.locationId,
                    });
                    this.vehicleForm.patchValue({
                      associateId: newAssociate.id,
                      name: `${newAssociate.lastName}, ${newAssociate.firstName}`,
                    });
                  });

                let customer: Customer = {
                  ...new Customer(),
                  associateId: newAssociate.id,
                };

                this.apiService.post<Customer>(`v/1/associates/customers`, customer).subscribe((customer) => {});
              }
            });
        }
      });
    } else {
      if (filteredAssociates) {
        let selectedAssociate = filteredAssociates.find((associate) => associate.id == value);
        this.selectedAssoc = selectedAssociate;
        this.associateForm.patchValue(
          {
            ...this.associateForm,
            id: `${value}`,
            firstName: `${selectedAssociate.firstName}`,
            lastName: `${selectedAssociate.lastName}`,
            emailAddress: `${selectedAssociate.emailAddress}`,
            classId: selectedAssociate.classId,
            messagePreferenceId: selectedAssociate.messagePreferenceId,
          },
          { emitEvent: false }
        );
        this.addressForm.patchValue({
          ...this.addressForm,
          associateId: value,
          addressLine1: selectedAssociate.addressLine1,
          homePhoneNumber: selectedAssociate.homePhoneNumber,
          cellPhoneNumber: selectedAssociate.cellPhoneNumber,
          countryId: selectedAssociate.countryId,
          postalZipCode: selectedAssociate.postalZipCode,
          provStateId: selectedAssociate.provStateId,
          locationId: selectedAssociate.locationId,
        });
        this.vehicleForm.patchValue({
          associateId: value,
          name: selectedAssociate.firstName
            ? `${selectedAssociate.lastName}, ${selectedAssociate.firstName}`
            : `${selectedAssociate.fullName}`,
        });
      }
    }

    this.filteredAssociates = [];
  }

  saveTypedName(name: string) {
    this.typedName = name;
  }
  ngOnDestroy() {
    this.updateAssociateSub.unsubscribe();
    this.updateAddressSub.unsubscribe();
    this.updateVehicleSub.unsubscribe();
    this.createAppointmentSub.unsubscribe();
    this.deleteAppointmentSub.unsubscribe();
    this.listenForCreateAppointmentOnServerSuccessSub.unsubscribe();
    if (this.associateFormCombinedSub) {
      this.associateFormCombinedSub.unsubscribe();
    }
  }
}
